import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  Tabs,
  Tab,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  List,
  ListItem,
  ListItemText,
  TableContainer,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  getPersonnel,
  createPerson,
  updatePerson,
  deletePerson,
  getCompanies,
  getPersonnelHoursForPerson,
  getAbsencesForPerson,
  createAbsence,
  deleteAbsence,
  getPersonnelFileCategories,
  createPersonnelFileCategory,
  updatePersonnelFileCategory,
  deletePersonnelFileCategory,
  getPersonnelFiles,
  getPersonnelFileUploadUrl,
  createPersonnelFile,
  updatePersonnelFile,
  deletePersonnelFile,
} from '../services/api';
import DateInputWithArrows from '../components/DateInputWithArrows';
import UpdateFileDueDateDialog from '../components/UpdateFileDueDateDialog';
import UpdateFileNotesDialog from '../components/UpdateFileNotesDialog';
import { formatDateEuropean } from '../utils/dateUtils';
import { formatPersonnelName } from '../utils/personnelDisplay';
import useResponsive from '../components/responsive/useResponsive';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import { useTranslation } from 'react-i18next';

function Personnel() {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();
  const [personnel, setPersonnel] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingPerson, setEditingPerson] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    email: '',
    phone: '',
    companyId: '',
    role: '',
    workVisaDate: '',
    notes: '',
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsPerson, setDetailsPerson] = useState(null);
  const [detailsTab, setDetailsTab] = useState(0);
  const [hours, setHours] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [absenceForm, setAbsenceForm] = useState({ type: 'VACATION', startDate: '', endDate: '', notes: '' });
  const [personFiles, setPersonFiles] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [renamingCategoryId, setRenamingCategoryId] = useState(null);
  const [renameCategoryName, setRenameCategoryName] = useState('');
  const [uploadDialog, setUploadDialog] = useState({
    open: false,
    categoryId: null,
    file: null,
    expirationDate: '',
    notes: '',
  });
  const [dueDateDialog, setDueDateDialog] = useState({ open: false, file: null });
  const [savingDueDate, setSavingDueDate] = useState(false);
  const [notesDialog, setNotesDialog] = useState({ open: false, file: null });
  const [savingNotes, setSavingNotes] = useState(false);

  const columns = [
    { field: 'firstName', headerName: t('personnel.fields.firstName'), width: 150 },
    { field: 'lastName', headerName: t('personnel.fields.lastName'), width: 150 },
    { field: 'nickname', headerName: t('personnel.fields.nickname'), width: 130 },
    { 
      field: 'company',
      headerName: t('personnel.fields.company'),
      width: 200,
      valueGetter: (params) => params.row.company?.name || '-',
    },
    { field: 'role', headerName: t('personnel.fields.role'), width: 150 },
    { field: 'email', headerName: t('personnel.fields.email'), width: 180 },
    { field: 'phone', headerName: t('personnel.fields.phone'), width: 150 },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 300,
      renderCell: (params) => (
        <Box>
          <Button onClick={() => handleEdit(params.row)}>{t('common.edit')}</Button>
          <Button onClick={() => handleViewDetails(params.row)}>{t('common.details')}</Button>
          <Button color="error" onClick={() => handleDelete(params.row.id)}>{t('common.delete')}</Button>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchPersonnel();
    fetchCompanies();
    fetchFileCategories();
  }, []);

  useEffect(() => {
    if (loading) return;
    const openId = Number(searchParams.get('open'));
    if (!openId) return;
    if (detailsOpen && detailsPerson?.id === openId) return;
    const person = personnel.find((item) => item.id === openId);
    if (person) {
      handleViewDetails(person, 3);
    }
  }, [loading, personnel, searchParams, detailsOpen, detailsPerson]);

  const fetchPersonnel = async () => {
    try {
      setLoading(true);
      const response = await getPersonnel();
      setPersonnel(response.data);
    } catch (error) {
      console.error('Error fetching personnel:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchFileCategories = async () => {
    try {
      const response = await getPersonnelFileCategories();
      return response.data || [];
    } catch (error) {
      console.error('Error fetching file categories:', error);
      return [];
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditingPerson(null);
    setFormData({
      firstName: '',
      lastName: '',
      nickname: '',
      email: '',
      phone: '',
      companyId: '',
      role: '',
      workVisaDate: '',
      notes: '',
    });
  };

  const handleEdit = (person) => {
    setEditingPerson(person);
    setFormData({
      firstName: person.firstName,
      lastName: person.lastName,
      nickname: person.nickname || '',
      email: person.email || '',
      phone: person.phone || '',
      companyId: person.companyId,
      role: person.role,
      workVisaDate: person.workVisaDate ? String(person.workVisaDate).slice(0, 10) : '',
      notes: person.notes || '',
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deletePerson(id);
      fetchPersonnel();
    } catch (error) {
      console.error('Error deleting person:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        companyId: parseInt(formData.companyId),
        workVisaDate: formData.workVisaDate ? new Date(formData.workVisaDate).toISOString() : undefined,
        nickname: formData.nickname?.trim() ? formData.nickname.trim() : null,
        email: formData.email?.trim() ? formData.email.trim() : null,
        phone: formData.phone?.trim() ? formData.phone.trim() : null,
      };
      if (editingPerson) {
        await updatePerson(editingPerson.id, payload);
      } else {
        await createPerson(payload);
      }
      handleClose();
      fetchPersonnel();
    } catch (error) {
      console.error('Error saving person:', error);
    }
  };

  const handleViewDetails = async (person, tab = 0) => {
    setDetailsPerson(person);
    setDetailsTab(tab);
    setDetailsOpen(true);
    try {
      const [hoursRes, absRes] = await Promise.all([
        getPersonnelHoursForPerson(person.id),
        getAbsencesForPerson(person.id),
      ]);
      setHours(hoursRes.data || []);
      setAbsences(absRes.data || []);
      await fetchPersonFiles(person.id);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchPersonFiles = async (personnelId) => {
    try {
      const response = await getPersonnelFiles(personnelId);
      setPersonFiles(response.data || []);
    } catch (error) {
      console.error('Error fetching personnel files:', error);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      await createPersonnelFileCategory({ name });
      setNewCategoryName('');
      await fetchFileCategories();
      if (detailsPerson) await fetchPersonFiles(detailsPerson.id);
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const handleRenameCategory = async (categoryId) => {
    const name = renameCategoryName.trim();
    if (!name) return;
    try {
      await updatePersonnelFileCategory(categoryId, { name });
      setRenamingCategoryId(null);
      setRenameCategoryName('');
      await fetchFileCategories();
      if (detailsPerson) await fetchPersonFiles(detailsPerson.id);
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await deletePersonnelFileCategory(categoryId);
      await fetchFileCategories();
      if (detailsPerson) await fetchPersonFiles(detailsPerson.id);
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const handleUploadFile = async (categoryId, file, expirationDate, notes) => {
    if (!detailsPerson || !file) return;
    try {
      const presignResponse = await getPersonnelFileUploadUrl(detailsPerson.id, {
        categoryId,
        originalName: file.name,
        contentType: file.type || 'application/octet-stream',
      });
      const { uploadUrl, objectKey } = presignResponse.data;
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      await createPersonnelFile(detailsPerson.id, {
        categoryId,
        objectKey,
        originalName: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size || 0,
        expirationDate: expirationDate || undefined,
        notes: notes || undefined,
      });
      await fetchPersonFiles(detailsPerson.id);
      await fetchFileCategories();
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const openUploadDialog = (categoryId, file) => {
    setUploadDialog({
      open: true,
      categoryId,
      file,
      expirationDate: '',
      notes: '',
    });
  };

  const closeUploadDialog = () => {
    setUploadDialog({
      open: false,
      categoryId: null,
      file: null,
      expirationDate: '',
      notes: '',
    });
  };

  const confirmUploadWithExpiration = async () => {
    if (!uploadDialog.categoryId || !uploadDialog.file) return;
    await handleUploadFile(
      uploadDialog.categoryId,
      uploadDialog.file,
      uploadDialog.expirationDate,
      uploadDialog.notes,
    );
    closeUploadDialog();
  };

  const handleDeleteFile = async (fileId) => {
    if (!detailsPerson) return;
    try {
      await deletePersonnelFile(fileId);
      await fetchPersonFiles(detailsPerson.id);
      await fetchFileCategories();
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const closeDueDateDialog = () => {
    if (savingDueDate) return;
    setDueDateDialog({ open: false, file: null });
  };

  const handleSaveDueDate = async (expirationDate) => {
    if (!dueDateDialog.file?.id) return;
    setSavingDueDate(true);
    try {
      await updatePersonnelFile(dueDateDialog.file.id, { expirationDate });
      setDueDateDialog({ open: false, file: null });
      if (detailsPerson) await fetchPersonFiles(detailsPerson.id);
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    } finally {
      setSavingDueDate(false);
    }
  };

  const closeNotesDialog = () => {
    if (savingNotes) return;
    setNotesDialog({ open: false, file: null });
  };

  const handleSaveNotes = async (notes) => {
    if (!notesDialog.file?.id) return;
    setSavingNotes(true);
    try {
      await updatePersonnelFile(notesDialog.file.id, { notes });
      setNotesDialog({ open: false, file: null });
      if (detailsPerson) await fetchPersonFiles(detailsPerson.id);
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const addAbsence = async () => {
    if (!detailsPerson) return;
    if (!absenceForm.startDate || !absenceForm.endDate) return;
    try {
      await createAbsence({
        personnelId: detailsPerson.id,
        type: absenceForm.type,
        startDate: absenceForm.startDate,
        endDate: absenceForm.endDate,
        notes: absenceForm.notes || undefined,
      });
      const absRes = await getAbsencesForPerson(detailsPerson.id);
      setAbsences(absRes.data || []);
      setAbsenceForm({ type: 'VACATION', startDate: '', endDate: '', notes: '' });
    } catch (e) {
      console.error(e);
    }
  };

  const removeAbsence = async (id) => {
    try {
      await deleteAbsence(id);
      const absRes = await getAbsencesForPerson(detailsPerson.id);
      setAbsences(absRes.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">{t('personnel.title')}</Typography>
        <Button variant="contained" onClick={handleOpen}>{t('personnel.addPerson')}</Button>
      </Box>

      {isMobile ? (
        <ResponsiveDataList
          rows={personnel}
          getTitle={(person) => formatPersonnelName(person)}
          getSubtitle={(person) => person.company?.name || '-'}
          renderFields={(person) => (
            <Typography variant="body2"><b>{t('personnel.fields.role')}:</b> {person.role}</Typography>
          )}
          renderActions={(person) => (
            <>
              <Button size="small" onClick={() => handleEdit(person)}>{t('common.edit')}</Button>
              <Button size="small" onClick={() => handleViewDetails(person)}>{t('common.details')}</Button>
              <Button size="small" color="error" onClick={() => handleDelete(person.id)}>{t('common.delete')}</Button>
            </>
          )}
        />
      ) : (
        <DataGrid
          rows={personnel}
          columns={columns}
          pageSize={10}
          loading={loading}
          autoHeight
        />
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle>{editingPerson ? t('personnel.editPerson') : t('personnel.addPerson')}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField fullWidth margin="normal" name="firstName" label={t('personnel.fields.firstName')} value={formData.firstName} onChange={handleChange} required />
            <TextField fullWidth margin="normal" name="lastName" label={t('personnel.fields.lastName')} value={formData.lastName} onChange={handleChange} required />
            <TextField fullWidth margin="normal" name="nickname" label={t('personnel.fields.nickname')} value={formData.nickname} onChange={handleChange} placeholder={t('common.optional')} />
            <TextField fullWidth margin="normal" name="companyId" label={t('personnel.fields.company')} select value={formData.companyId} onChange={handleChange} required>
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth margin="normal" name="role" label={t('personnel.fields.role')} value={formData.role} onChange={handleChange} required />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">{t('personnel.contact')}</Typography>
            <TextField fullWidth margin="normal" name="email" type="email" label={t('personnel.fields.email')} value={formData.email} onChange={handleChange} placeholder={t('common.optional')} />
            <TextField fullWidth margin="normal" name="phone" type="tel" label={t('personnel.fields.phone')} value={formData.phone} onChange={handleChange} placeholder={t('common.optional')} />
            <Divider sx={{ my: 2 }} />
            <DateInputWithArrows fullWidth margin="normal" name="workVisaDate" label={t('personnel.fields.workVisaDate')} value={formData.workVisaDate} onChange={handleChange} />
            <TextField fullWidth margin="normal" name="notes" label={t('personnel.fields.notes')} value={formData.notes} onChange={handleChange} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">{t('common.save')}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          if (searchParams.get('open')) {
            setSearchParams({}, { replace: true });
          }
        }}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle>{t('personnel.personDetails')}</DialogTitle>
        <DialogContent>
          <Tabs value={detailsTab} onChange={(_, v) => setDetailsTab(v)} sx={{ mb: 2 }}>
            <Tab label={t('personnel.tabs.info')} />
            <Tab label={t('personnel.tabs.hours')} />
            <Tab label={t('personnel.tabs.absences')} />
            <Tab label={t('personnel.tabs.files')} />
          </Tabs>
          {detailsTab === 0 && detailsPerson && (
            <Box>
              <Typography><b>{t('projects.fields.name')}:</b> {formatPersonnelName(detailsPerson)}</Typography>
              <Typography><b>{t('personnel.fields.company')}:</b> {detailsPerson.company?.name || '-'}</Typography>
              <Typography><b>{t('personnel.fields.role')}:</b> {detailsPerson.role}</Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>{t('personnel.contact')}</Typography>
              <Typography><b>{t('personnel.fields.email')}:</b> {detailsPerson.email || '-'}</Typography>
              <Typography><b>{t('personnel.fields.phone')}:</b> {detailsPerson.phone || '-'}</Typography>
              {detailsPerson.user?.email ? (
                <Typography><b>{t('personnel.linkedUser')}:</b> {detailsPerson.user.email}</Typography>
              ) : null}
              <Typography><b>{t('personnel.fields.workVisaDate')}:</b> {detailsPerson.workVisaDate ? formatDateEuropean(detailsPerson.workVisaDate) : '-'}</Typography>
              <Typography><b>{t('personnel.fields.notes')}:</b> {detailsPerson.notes || '-'}</Typography>
            </Box>
          )}
          {detailsTab === 1 && (
            <Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('personnel.fields.date')}</TableCell>
                    <TableCell align="right">{t('personnel.fields.hours')}</TableCell>
                    <TableCell>{t('personnel.fields.project')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hours.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell>{formatDateEuropean(h.date)}</TableCell>
                      <TableCell align="right">{h.hours}</TableCell>
                      <TableCell>{h.project?.name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </TableContainer>
            </Box>
          )}
          {detailsTab === 2 && (
            <Box>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField label={t('personnel.fields.type')} select value={absenceForm.type} onChange={(e) => setAbsenceForm({ ...absenceForm, type: e.target.value })} sx={{ minWidth: 160 }}>
                  <MenuItem value="VACATION">{t('personnel.absence.vacation')}</MenuItem>
                  <MenuItem value="SICK_LEAVE">{t('personnel.absence.sickLeave')}</MenuItem>
                </TextField>
                <DateInputWithArrows label={t('personnel.fields.startDate')} value={absenceForm.startDate} onChange={(e) => setAbsenceForm({ ...absenceForm, startDate: e.target.value })} />
                <DateInputWithArrows label={t('personnel.fields.endDate')} value={absenceForm.endDate} onChange={(e) => setAbsenceForm({ ...absenceForm, endDate: e.target.value })} />
                <TextField label={t('personnel.fields.notes')} value={absenceForm.notes} onChange={(e) => setAbsenceForm({ ...absenceForm, notes: e.target.value })} />
                <Button variant="contained" onClick={addAbsence}>{t('common.add')}</Button>
              </Stack>
              <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('personnel.fields.type')}</TableCell>
                    <TableCell>{t('personnel.fields.start')}</TableCell>
                    <TableCell>{t('personnel.fields.end')}</TableCell>
                    <TableCell>{t('personnel.fields.notes')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {absences.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.type === 'VACATION' ? t('personnel.absence.vacation') : t('personnel.absence.sickLeave')}</TableCell>
                      <TableCell>{formatDateEuropean(a.startDate)}</TableCell>
                      <TableCell>{formatDateEuropean(a.endDate)}</TableCell>
                      <TableCell>{a.notes || ''}</TableCell>
                      <TableCell align="right"><Button color="error" size="small" onClick={() => removeAbsence(a.id)}>{t('common.delete')}</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </TableContainer>
            </Box>
          )}
          {detailsTab === 3 && detailsPerson && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('personnel.files.globalCategories')}</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                <TextField
                  label={t('personnel.fields.newCategory')}
                  size="small"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
                <Button variant="contained" onClick={handleCreateCategory}>{t('common.create')}</Button>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              {(personFiles || []).map((entry) => (
                <Box key={entry.category.id} sx={{ mb: 2, border: '1px solid #eee', borderRadius: 1, p: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
                    {renamingCategoryId === entry.category.id ? (
                      <>
                        <TextField
                          size="small"
                          value={renameCategoryName}
                          onChange={(e) => setRenameCategoryName(e.target.value)}
                        />
                        <Button size="small" onClick={() => handleRenameCategory(entry.category.id)}>{t('common.save')}</Button>
                        <Button size="small" onClick={() => { setRenamingCategoryId(null); setRenameCategoryName(''); }}>{t('common.cancel')}</Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="subtitle1">{entry.category.name}</Typography>
                        <Button size="small" onClick={() => { setRenamingCategoryId(entry.category.id); setRenameCategoryName(entry.category.name); }}>{t('common.rename')}</Button>
                        <Button size="small" color="error" onClick={() => handleDeleteCategory(entry.category.id)}>{t('common.delete')}</Button>
                      </>
                    )}
                  </Stack>
                  <Button variant="outlined" component="label" size="small" sx={{ mb: 1 }}>
                    {t('personnel.files.uploadFile')}
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        const selectedFile = e.target.files?.[0];
                        if (selectedFile) openUploadDialog(entry.category.id, selectedFile);
                        e.target.value = '';
                      }}
                    />
                  </Button>
                  {(entry.files || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('personnel.files.noFilesInCategory')}</Typography>
                  ) : (
                    <List dense>
                      {entry.files.map((file) => (
                        <ListItem
                          key={file.id}
                          alignItems="flex-start"
                          sx={{ flexDirection: 'column', alignItems: 'stretch', px: 0 }}
                        >
                          <ListItemText
                            primary={file.originalName}
                            secondary={`${t('personnel.files.uploadedAt')}: ${new Date(file.uploadedAt).toLocaleString(i18n.language)}${file.expirationDate ? ` | ${t('personnel.files.expires')}: ${formatDateEuropean(file.expirationDate)}` : ''}${file.notes ? ` | ${file.notes}` : ''}`}
                          />
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                            <Button size="small" href={file.url} target="_blank" rel="noreferrer">{t('common.open')}</Button>
                            <Button size="small" onClick={() => setDueDateDialog({ open: true, file })}>{t('common.updateDueDate')}</Button>
                            <Button size="small" onClick={() => setNotesDialog({ open: true, file })}>{t('common.updateNotes')}</Button>
                            <Button size="small" color="error" onClick={() => handleDeleteFile(file.id)}>{t('common.delete')}</Button>
                          </Stack>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDetailsOpen(false);
              if (searchParams.get('open')) {
                setSearchParams({}, { replace: true });
              }
            }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={uploadDialog.open} onClose={closeUploadDialog} fullWidth maxWidth="xs" fullScreen={isMobile}>
        <DialogTitle>{t('personnel.files.uploadFile')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {uploadDialog.file ? `${t('personnel.files.fileLabel')}: ${uploadDialog.file.name}` : t('personnel.files.selectExpiration')}
          </Typography>
          <DateInputWithArrows
            fullWidth
            label={t('personnel.fields.expirationDateOptional')}
            value={uploadDialog.expirationDate}
            onChange={(e) => setUploadDialog((prev) => ({ ...prev, expirationDate: e.target.value }))}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            label={t('common.notes')}
            value={uploadDialog.notes}
            onChange={(e) => setUploadDialog((prev) => ({ ...prev, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadDialog}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={confirmUploadWithExpiration}>{t('common.upload')}</Button>
        </DialogActions>
      </Dialog>
      <UpdateFileDueDateDialog
        open={dueDateDialog.open}
        onClose={closeDueDateDialog}
        currentExpirationDate={dueDateDialog.file?.expirationDate}
        onSave={handleSaveDueDate}
        saving={savingDueDate}
      />
      <UpdateFileNotesDialog
        open={notesDialog.open}
        onClose={closeNotesDialog}
        currentNotes={notesDialog.file?.notes}
        onSave={handleSaveNotes}
        saving={savingNotes}
      />
    </Box>
  );
}

export default Personnel; 